import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

// Import Swiper styles (assuming bundle is needed globally or imported where UISlider is used)
// If not imported globally, uncomment the line below:
// import 'swiper/swiper-bundle.css';

// --- Styled Components (Moved from NewsList.js) ---

const SliderMaxWidth = styled.div`
  position: relative;
  padding: 0 2rem; // Default padding, can be overridden via style prop if needed
  width: 100%;
  margin: 0 auto;

  ${props => props.theme.bp.below.lg} {
    padding: 1rem 1rem 0 1rem;
  }
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-family: ${(props) => props.theme.fonts.body};
    text-align: left;
    margin-bottom: 0;
    font-size: 2rem;
    font-family: ${(props) => props.theme.fonts.headings};
  }
`;

const ArrowContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

// Using the final style from NewsList (white bg, black arrow)
const ArrowButtonBase = styled.button`
  background: white;
  border: 1px solid #eee;
  border-radius: 50%;
  width: 2rem; // Use consistent units
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: black;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
  }

  svg {
    width: 1rem;
    height: 1rem;
    fill: currentColor;
  }

  &.swiper-button-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrevButtonStyled = styled(ArrowButtonBase)`
  svg {
    transform: rotate(180deg);
  }
`;

// Explicitly define NextButtonStyled (even if just inheriting base) for clarity
const NextButtonStyled = styled(ArrowButtonBase)``;

const SliderFlex = styled.div`
  width: 100%;
  overflow: hidden;
`;

// New styled component to wrap Swiper and apply height styles
const SwiperWrapperStyled = styled.div`
  .swiper-wrapper {
    display: flex; // Use flexbox for the wrapper
    align-items: stretch; // Stretch items vertically
  }

  .swiper-slide {
    height: auto; // Allow slide height to be determined by flex context
    display: flex; // Make slide a flex container
    flex-direction: column; // Stack content vertically within slide
    > * { // Target the direct child (e.g., the Link wrapping SinglePost)
        height: 100%; // Make the child fill the slide height
    }
  }
`;

// --- The Reusable Slider Component ---

const UISlider = ({ title, children, swiperConfig }) => {
  // Refs for navigation buttons
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const swiperRef = useRef(null); // Ref for the Swiper instance

  const defaultSwiperConfig = {
    modules: [Navigation],
    spaceBetween: 30,
    slidesPerView: 3,
    breakpoints: {
      320: { slidesPerView: 1, spaceBetween: 10 },
      768: { slidesPerView: 2, spaceBetween: 20 },
      1024: { slidesPerView: 3, spaceBetween: 30 },
    },
    style: { padding: '1rem 0' }
  };

  const finalSwiperConfig = { ...defaultSwiperConfig, ...swiperConfig };
  // Merge breakpoints and style, but exclude navigation initially
    if (swiperConfig?.breakpoints) {
      finalSwiperConfig.breakpoints = { ...defaultSwiperConfig.breakpoints, ...swiperConfig.breakpoints };
    }
    if (swiperConfig?.style) {
      finalSwiperConfig.style = { ...defaultSwiperConfig.style, ...swiperConfig.style };
    }

    // Remove navigation property if it exists from swiperConfig to avoid conflicts
    // It will be set manually using refs
    delete finalSwiperConfig.navigation;

  // Use useEffect to initialize Swiper with refs after elements are mounted
  useEffect(() => {
    if (swiperRef.current && prevButtonRef.current && nextButtonRef.current) {
      const swiperInstance = swiperRef.current.swiper;
      if (swiperInstance) {
        // Assign navigation elements directly to the initialized Swiper instance
        swiperInstance.params.navigation.prevEl = prevButtonRef.current;
        swiperInstance.params.navigation.nextEl = nextButtonRef.current;
        // Re-init navigation
        swiperInstance.navigation.destroy(); 
        swiperInstance.navigation.init();
        swiperInstance.navigation.update();
      }
    }
    // Dependency array ensures this runs when refs change, though ideally only once after mount
  }, [children]); // Re-run if children change, might need adjustment depending on use case


  const ArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M443.3 267.3c6.2-6.2 6.2-16.4 0-22.6l-176-176c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L393.4 240 16 240c-8.8 0-16 7.2-16 16s7.2 16 16 16l377.4 0L244.7 420.7c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l176-176z"/></svg>
  );

  return (
    <SliderMaxWidth>
      <SliderHeader>
        {title && <h2>{title}</h2>}
        <ArrowContainer>
          {/* Assign refs to the button components */}
          <PrevButtonStyled ref={prevButtonRef}>
            <ArrowIcon />
          </PrevButtonStyled>
          <NextButtonStyled ref={nextButtonRef}>
            <ArrowIcon />
          </NextButtonStyled>
        </ArrowContainer>
      </SliderHeader>

      <SliderFlex>
        {/* Wrap Swiper in the new styled component */}
        <SwiperWrapperStyled>
          <Swiper ref={swiperRef} {...finalSwiperConfig}>
            {React.Children.map(children, (child, index) => (
              <SwiperSlide key={index}>
                {child}
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperWrapperStyled>
      </SliderFlex>
    </SliderMaxWidth>
  );
};

export default UISlider; 